/**
 * FroggerAIChatPanel — THE chat, with no chrome of its own.
 *
 * I-03 R6 · S3-06.3. The dock and the standalone FroggerAI modal are two
 * FRAMES around ONE conversation surface, and this is that surface. It
 * was lifted out of `FroggerAIChatStreaming.jsx` whole — same stream,
 * same conversation id, same synchronous send guard, same read-back row
 * and Undo — because a dock with its own copy of the chat would be a
 * second place for the conversation id, the double-submit guard and the
 * readback contract to be got wrong, and they would drift the first time
 * one of them was fixed.
 *
 * What the frame supplies:
 *   `facilityId`        — resolved by the frame (route params, context).
 *   `place`             — the dock's current place; sent on EVERY turn.
 *   `compact`           — the phone/dock layout: no welcome paragraph,
 *                         no build tag, a single-row composer.
 *   `initialUtterance`  — a turn to send as soon as it is non-empty
 *                         (Tasks Today's "Walk me through it" arrives
 *                         this way, via the dock's `froggerai:ask`
 *                         listener), paired with `utteranceKey`, which
 *                         the frame bumps per dispatch so the SAME words
 *                         asked twice are two turns rather than one
 *                         swallowed one.
 *   `onNavigate`        — what to do with a `navigate` chunk.
 *
 * Vocabulary: enclosure / tank / rack / room. Never the other word.
 */

import React, {
  useState, useEffect, useRef, useCallback, useContext,
} from 'react';
import { Form, Alert } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { streamingAPI } from '../../services/streamingAPI';
import { STREAM_SILENCE_MS, NO_ANSWER_TEXT, DROPPED_TEXT } from '../../services/streamWatchdog';
import { getAISummary, sendVoiceMessage, getVoiceConfig } from '../../aiApi';
import { newTurnId } from '../../utils/turnId';
import { announceWrite } from '../../utils/froggerWrites';
import VoiceRecorder from './VoiceRecorder';
import { placeForWire } from '../../context/PlaceContext';
import { AuthContext } from '../../context/AuthContext';
import { froggerKeys, userIdOf } from '../../utils/froggerStorageKeys';
import { roleAt } from '../../pages/facilities/training/trainingText';
import { scrollIntoViewMotionSafe } from '../../utils/scrollHelpers';
import { ChatMarkdown } from '../FroggyChatBubble/chatMarkdown';
import FroggyWorking, {
  FROGGER_WORKING_LINES, FROGGER_WORKING_NEUTRAL, FROGGER_WORKING_SR,
} from '../FroggyChatBubble/FroggyWorking';
// The panel owns its own look, so the dock and the modal cannot render
// the same conversation two different ways (the SchemaField lesson:
// one renderer, and each HOST imports the frame CSS it needs itself).
import './FroggerAIChatPanel.css';

/** How much of the transcript is written down. See the note below. */
const LOG_KEEP = 50;

/**
 * What a RESTORED turn shows when the server stored no assistant text.
 *
 * ⚠️ ONE SHORT SENTENCE, AND IT DOES NOT NAME THE TOOL (re-sweep #6,
 * item 5). This was "Done — action completed." in two places here and
 * "Done — the log event action completed." on the server, and all three
 * rendered immediately above the real read-back row ("Logged shed on
 * Kiwi." with an Undo on it) — two receipts for one write, the first of
 * them the plumbing narrating itself.
 *
 * It cannot be empty: #161 is that a blank assistant bubble after a real
 * mutation invites the keeper to retry a non-idempotent action. It is
 * the shortest true thing, and it MATCHES the server's
 * `memory.TERMINAL_TOOL_FALLBACK`, so a reloaded transcript reads the
 * same as the live one.
 */
const SILENT_TURN_TEXT = 'Done.';

/**
 * A FAILED TURN IS STILL A TURN (review of batch 8, MAJOR 2).
 *
 * `ERROR_TEXT` is the bubble's fallback when the transport gave no
 * reason at all. `ERROR_PREVIEW` is the one line a COLLAPSED dock shows
 * — deliberately not the raw message, because "Error: NetworkError when
 * attempting to fetch resource." on a 140-character preview line tells a
 * keeper with wet hands nothing they can act on, and the real text is
 * one tap away in the transcript.
 */
/**
 * Two chunks of one turn, joined so they never RUN TOGETHER (Cowork run
 * 23, finding 13).
 *
 * FroggerAI's server emits a chunk per graph step — each is a WHOLE
 * sentence the model wrote on one iteration ("I'll check that.", then
 * "Let me look at the rack.", then the answer) — and the panel appended
 * them edge to edge, so three filler lines read as one word-glued run.
 * A BLANK LINE goes between two chunks unless one side already carries
 * the whitespace (Cowork run 24, closure item 7). The dock renders the
 * answer as MARKDOWN now (finding 8), and a single `\n` is a soft break
 * there — the two sentences ran back into one paragraph. `\n\n` is a
 * paragraph break, so each chunk is its own paragraph, which is what it
 * is: one model iteration's whole sentence.
 */
export const joinChunks = (prev, next) => {
  const before = prev || '';
  if (!before) return next || '';
  if (!next) return before;
  if (/\s$/.test(before) || /^\s/.test(next)) return before + next;
  return `${before}\n\n${next}`;
};

/**
 * ⚠️ **THE DOCK ASSEMBLES A MESSAGE, THEN RENDERS IT** (Cowork run 25,
 * F2 — a regression from run 24's blank-line join).
 *
 * `joinChunks` was written on the belief that every chunk is one whole
 * model iteration. It is not: `call_model` splits each text block into
 * 100-character FRAGMENTS (`nodes.py`, "split for better streaming
 * UX"), and the JSON fallbacks split an answer into 50/100-character
 * pieces the same way. So a blank line went in mid-word — "gr / oup",
 * "starti / ng" — and a `**bold**` that straddled two fragments became
 * two paragraphs with a raw `**` in each.
 *
 * The real shape of a turn is: fragments of ONE iteration's narration
 * arrive back to back, and a new iteration begins only after the model
 * called a tool — a `tool_call` / `tool_result` chunk sits between the
 * two runs of narration. A `synthesized` chunk (the server's own
 * receipt, question or fallback) is a sentence of its own too. So:
 *
 *  * fragments are concatenated RAW;
 *  * `joinChunks`' paragraph break goes in ONLY at an iteration
 *    boundary (`boundary` true) — run 23's rule that two narrations do
 *    not run together still holds, where there ARE two narrations;
 *  * Markdown is rendered ONCE over the assembled message, so bold that
 *    spans fragments renders as one `<strong>`.
 */
export const assembleChunks = (prev, next, boundary) => {
  if (!boundary) return `${prev || ''}${next || ''}`;
  // At a boundary a trailing or leading SPACE is not a paragraph break,
  // so it is trimmed before the join: "Let me check. " then a tool call
  // then "Found it." is two paragraphs, never "Let me check. Found it.".
  const before = String(prev || '').replace(/\s+$/, '');
  const after = String(next || '').replace(/^\s+/, '');
  return joinChunks(before, after);
};

/**
 * ⚠️ **ONE CHIP PER TOOL CALL, AND ONE PER QUESTION** (Cowork run 25,
 * F8). A chip is added per `tool_result` chunk. The server's
 * `tool_result` carries no id (only `tool_call` does), so the panel
 * pairs each result with the id its `tool_call` announced, in order,
 * per tool name; a result whose id is already on the row is a REPLAY
 * and adds nothing. And a tool that stopped to ASK twice in one turn —
 * the model re-calling `recordPairing` after the first call asked — is
 * one question on screen (the server's question supersedes the rest),
 * so it is one `recordPairing?` chip, not two. A call that WROTE is
 * never collapsed: two creates are two rows.
 */
export const addToolChip = (tools, entry) => {
  const list = tools || [];
  const chip = toolChip(entry);
  if (!chip.name) return list;
  const on = list.map(toolChip);
  if (chip.id && on.some((t) => t.id === chip.id)) return list;
  if (chip.asked && on.some((t) => t.asked && t.name === chip.name)) return list;
  return [...list, chip.id
    ? { name: chip.name, asked: chip.asked, id: chip.id }
    : { name: chip.name, asked: chip.asked }].slice(-12);
};

export const ERROR_TEXT = 'Something went wrong.';
export const ERROR_PREVIEW = 'Something went wrong — tap to see';

// Re-exported so a reader of the panel finds the watchdog's two numbers
// where the panel uses them, and a test can pin the line from either
// side. The DEFINITION is `services/streamWatchdog` — see the header
// there for why it is a module of its own.
export { STREAM_SILENCE_MS, NO_ANSWER_TEXT, DROPPED_TEXT };

/** A 401: the login ran out. The only status that signs the keeper out. */
export const SIGNED_OUT_TEXT = 'Authentication failed. Please log in again.';
/**
 * A 403: a PERMISSION refusal (`HasAnyFacilityRoleOrIsAdmin`), never a
 * login problem — the token is good and is kept (review of 9ca56cb3).
 */
export const NO_ACCESS_TEXT = "You don't have access to this facility's assistant.";
/**
 * Voice-upload statuses that do NOT mean "refused before anything ran":
 *  - 502/503/504 — a PROXY gave up: gunicorn's `--timeout` is 120 s, so a
 *    voice turn killed there answers 502/504 after its writes may already
 *    have landed;
 *  - 500 — the voice view answers 500 when the GRAPH reported an error
 *    (its tools may already have written) and from its catch-all.
 * All four get `NO_ANSWER_TEXT`, never "Voice message failed: … Please
 * try again", which is an instruction to re-send a turn that may have
 * landed.
 */
export const GATEWAY_STATUSES = [500, 502, 503, 504];

/**
 * What a failed stream SAYS, from what the service knows about the
 * failure. ONE reading, so every error branch of a turn closes its bubble
 * with a sentence that is true:
 *  - 401 → signed out;
 *  - 403 → no access (token kept);
 *  - any other 4xx except 408 → the server certainly did not run it, so it
 *    was REFUSED — in the server's own sentence when it sent one;
 *  - a transport failure → "may still have gone through": `DROPPED_TEXT`
 *    appended when something had already arrived, `NO_ANSWER_TEXT` (the
 *    reason under it) when nothing had;
 *  - a server `error` chunk → the server's own sentence.
 * Returns `{ text, append, signOut }`.
 */
export function streamFailureText(err, { heardSomething = false } = {}) {
  const status = err?.status;
  const reason = `Error: ${err?.message || 'stream failed'}`;
  if (status === 401) return { text: SIGNED_OUT_TEXT, append: false, signOut: true };
  if (status === 403) return { text: NO_ACCESS_TEXT, append: false, signOut: false };
  if (status >= 400 && status < 500 && status !== 408) {
    return {
      text: err?.serverSentence || `That request was refused (HTTP ${status}).`,
      append: false,
      signOut: false,
    };
  }
  if (err?.transport) {
    return heardSomething
      ? { text: DROPPED_TEXT, append: true, signOut: false }
      : { text: `${NO_ANSWER_TEXT}\n\n${reason}`, append: false, signOut: false };
  }
  return { text: reason, append: false, signOut: false };
}

/**
 * THE NUMBERS ARE DATA, NOT PROSE (Cowork re-sweep of R6, item 3).
 *
 * "what's due today" answered "38 tasks still to do today" while the
 * page showed 104 overdue / 37 pending / 1 in progress. All three
 * buckets were in the tool result and the model repeated one — which is
 * what a model does with a list it was not asked to recite. So the
 * `readback` node emits a `counts` chunk for any turn that called
 * `whatsDue` or `startWalk`, and the row below renders it from the
 * SERVER's own numbers rather than from the sentence.
 *
 * ⚠️ ONE FALLBACK, AND IT IS THE SERVER'S RULE. `counts.text` is the
 * line `counts_line` already wrote, and it is used whenever it is
 * there. Only when it is missing is the same dot-joined line rebuilt
 * here — THREE FIXED SEGMENTS, zeros included, in the Tasks Today
 * header's own order, exactly as the server writes them — so the page
 * and the voice cannot end up describing one round two ways. A second
 * wording is how "FroggerAI says four, the page says five" starts all
 * over again.
 *
 * ⚠️ THE ZEROS STAY (operator ruling, Cowork re-sweep #4). This used to
 * drop an empty bucket, matching a server that also dropped it; both
 * sides now keep all three, because a row that changes SHAPE with the
 * data is read next to a header that never does. "142 overdue · 7
 * pending · 0 in progress" is the row, whatever the numbers are.
 *
 * ⚠️ AND THE ORDER IS OVERDUE FIRST (S3-06.4 review), because that is
 * the order `TasksTodayPage` draws its cards in. Both this list and the
 * server's `PAGE_BUCKETS` said pending first while both comments
 * claimed the header's order.
 */
const COUNT_BUCKETS = [
  ['overdue', 'overdue'],
  ['pending', 'pending'],
  ['in_progress', 'in progress'],
];

/**
 * One tool chip's `{name, asked}`, from either shape the log can hold.
 *
 * Chips were bare strings until the re-sweep #4 fix, and the transcript
 * survives a reload — so a browser that was open before this shipped
 * has string entries in `localStorage` and must not render `[object
 * Object]` or crash the row. Unknown shapes become an empty name, which
 * the row drops.
 */
export const toolChip = (entry) => {
  if (typeof entry === 'string') return { name: entry, asked: false };
  if (entry && typeof entry === 'object') {
    const chip = { name: String(entry.name || ''), asked: !!entry.asked };
    // The tool_call id, when the panel could pair one (run 25, F8).
    if (entry.id) chip.id = String(entry.id);
    return chip;
  }
  return { name: '', asked: false };
};

export const countsLine = (counts) => {
  if (!counts) return '';
  const given = String(counts.text || '').trim();
  if (given) return given;
  return COUNT_BUCKETS
    .map(([key, label]) => `${Number(counts[key]) || 0} ${label}`)
    .join(' · ');
};

/** Where one conversation's transcript lives: the signed-in USER,
 * facility and conversation (R-TRAINING junior fix 1 — a log written as
 * user 2 is never read as user 54). `userId` null is "nobody signed in". */
export const logStorageKey = (facilityId, conversationId, userId = null) => (
  froggerKeys.log(userId, facilityId, conversationId));

/**
 * THE TOOL CHIPS ARE OWNER/MANAGER ONLY (R-TRAINING junior Cowork run,
 * ruled 2026-11-04, fix 2). "Which tool answered that" is a debugging aid
 * for the people who debug; Staff — juniors included — never see a tool
 * name. A WHITELIST: a role the panel cannot read (no provider, no row
 * at this facility) hides them.
 */
const TOOL_CHIP_ROLES = ['Owner', 'Manager'];
export const seesToolChips = (roles, facilityId) => (
  TOOL_CHIP_ROLES.includes(roleAt(roles, facilityId)));

/** The composer's placeholder: short enough for the 1280 dock (run 33, D6). */
export const COMPOSER_PLACEHOLDER = 'Ask or log…';

/** A send refused while a reply runs (Cowork on df05fc19): nothing was
 * sent, and the words are still in the box. */
export const HELD_MID_TURN_TEXT = 'Not sent yet — still answering. Your message is still in the box; send it when this reply is done.';

/**
 * ⚠️ THE FRAMES NEED A HANDLE ON "START AGAIN" (Cowork re-sweep of R6,
 * 2026-09-11). S3-06.4 made the transcript survive a reload, which is
 * right — and left no way to put it down: every reload came back to the
 * same conversation and the same Redis session, with no "new chat"
 * anywhere on the screen. `handleNewConversation` has existed since #153
 * and nothing rendered a control for it.
 *
 * It is exposed through a ref rather than rendered here because the
 * control belongs in each FRAME's own header — the dock's `fd__head`,
 * the modal's `Modal.Header` — and a phone dock collapsed to ONE ROW
 * must not grow a toolbar to hold it.
 */

const FroggerAIChatPanel = React.forwardRef(({
  facilityId,
  facilityName,
  active,
  place,
  placeNowhere,
  compact,
  chipSlot,
  chipRow,
  initialUtterance,
  utteranceKey,
  onUtteranceSent,
  onNavigate,
  onAnswer,
  onPreview,
  onNewConversation,
  showWelcome,
  placeholder,
}, ref) => {
  const canSend = Boolean(facilityId);
  // Whose panel this is: the transcript and conversation id are kept per
  // signed-in user, and the tool chips follow the role at this facility.
  const auth = useContext(AuthContext) || {};
  const userId = userIdOf(auth.user);
  const showToolChips = seesToolChips(auth.roles, facilityId);

  // ── THE TRANSCRIPT IS PERSISTED (S3-06.4) ─────────────────────────
  //
  // Cowork QA of R6, 2026-09-11: the transcript and its Undo buttons
  // VANISH on a navigation or a reload — and Cowork lost an Undo that
  // way, leaving a QADELETE feeding on beta as residue. The dock exists
  // to be used while walking between racks, which is the one activity
  // that reloads and re-routes constantly, so an in-memory-only log is
  // a receipt that is gone by the time the keeper wants it.
  //
  // Keyed by FACILITY and CONVERSATION id, in `sessionStorage` — the
  // same scope the conversation id itself lives in, so "New
  // conversation" starts a fresh key and the old one is dropped rather
  // than orphaned. Only the last `LOG_KEEP` messages, and only the
  // fields that mean something tomorrow (`streaming` is a spinner, not
  // a fact).
  //
  // THE TOKENS ARE STILL THE SERVER'S TO JUDGE. What is restored is a
  // BUTTON; pressing it sends `undo <token>` as an ordinary turn and
  // the server checks the facility, the user and whether it has already
  // been undone (`undo._authorise`, `_find`, which reads the turn log
  // when Redis has forgotten). A stale button answers honestly; it does
  // not reverse anything twice.
  const storageKeyFor = (cid) => logStorageKey(facilityId, cid, userId);

  // Stable conversation ID - survives close/reopen (and now navigation,
  // which is the whole point of a dock) by living in sessionStorage.
  const conversationIdRef = useRef(null);
  if (!conversationIdRef.current) {
    const storageKey = froggerKeys.conversation(userId, facilityId);
    let storedCid = null;
    try { storedCid = sessionStorage.getItem(storageKey); } catch (e) { storedCid = null; }
    if (!storedCid) {
      storedCid = (window.crypto?.randomUUID?.() || `${Date.now()}`).slice(0, 16);
      try { sessionStorage.setItem(storageKey, storedCid); } catch (e) { /* private mode */ }
    }
    conversationIdRef.current = storedCid;
  }

  const readStoredLog = () => {
    try {
      const raw = sessionStorage.getItem(storageKeyFor(conversationIdRef.current));
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.messages)) return null;
      return parsed;
    } catch (e) { return null; }
  };

  // Read ONCE, on the first render. `readStoredLog` answers null when
  // there is nothing stored, so a "have I read it yet" flag is what
  // stops it re-reading `sessionStorage` on every render.
  const readLogRef = useRef(false);
  const restoredRef = useRef(null);
  if (!readLogRef.current) {
    readLogRef.current = true;
    const found = readStoredLog();
    if (found) {
      // MARKED, because `session: 'new'` drops exactly these and
      // nothing else — see `applyChunk`.
      restoredRef.current = {
        ...found,
        messages: found.messages.map((m) => ({ ...m, restored: true })),
      };
    }
  }
  const restored = restoredRef.current;

  // State
  const [messages, setMessages] = useState(
    () => (restored ? restored.messages : []));
  // Which undo tokens have already been spent. Restored with the log:
  // a button that says Undo after a reload, for something already
  // undone, is the one thing worse than no button.
  const [undoneTokens, setUndoneTokens] = useState(
    () => new Set(restored?.undone || []));
  const [draft, setDraft] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState(null);
  // True after a send was REFUSED because a reply was still running —
  // the message stays in the box and a line under the row says so
  // (Cowork on df05fc19; see `handleSubmit`). Cleared when the turn ends.
  const [heldMidTurn, setHeldMidTurn] = useState(false);
  const [activeToolCall, setActiveToolCall] = useState(null);
  // ⚠️ ONE STATUS TOKEN, AND THE MIC OWNS IT DURING A VOICE SESSION.
  //
  // Operator, phone pass on beta 2026-09-13: the collapsed dock said the
  // same thing three times at once — "Working" in the textarea,
  // "Thinking" on the mic button and "Thinking…" on a spinner row of its
  // own underneath — on the screen with the least room in the app. The
  // ruling: during a live voice session the token is the MIC BUTTON'S
  // LABEL, because it is already in the composer row and costs no row of
  // its own, and NOTHING ELSE says it.
  //
  // `VoiceRecorder` reports its visible status up here (`onStatusChange`,
  // null when it is idle, ended or paused — "Talk" and "Resume" are
  // affordances, not status), and the spinner row below stands down for
  // exactly as long as that is set. A TYPED turn has no mic label, so it
  // keeps the spinner row it has always had.
  const [voiceStatus, setVoiceStatus] = useState(null);
  const [voiceConfig, setVoiceConfig] = useState({
    maxDurationSeconds: 300,
    recommendedMaxSeconds: 75,
    maxFileSizeMB: 25,
  });

  // Refs
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const activeStreamRef = useRef(null);
  // See `sendUtterance`: the synchronous half of the double-send guard.
  const sendingRef = useRef(false);
  // ── THE WATCHDOG (Cowork run 13, F1) ───────────────────────────────
  //
  // ⚠️ **PER TURN, NOT PER PANEL** (review of 64044652, item 4 —
  // reproduced). This was one bare timer id shared by every turn, and
  // the sequence that broke it is ordinary: turn 1 is stopped with the
  // socket still open, turn 2 is sent, and a LATE chunk from turn 1
  // re-armed the shared ref with turn 1's own `stall` closure — so
  // turn 2 owned no timer at all and sat silent for eight minutes
  // unburied, which is the beta bug this feature exists to fix,
  // reintroduced by its own fix.
  //
  // `watchdogRef` therefore carries `{id, timer}` and `currentTurnRef`
  // says which turn OWNS THE COMPOSER. Every late callback asks first:
  // a turn that is no longer current may still write its own bubble
  // (the burial is provisional — see `sendUtterance`) but may never arm
  // a timer, release the send guard, or drop the live turn's stream
  // handle.
  const watchdogRef = useRef(null);
  const currentTurnRef = useRef(null);
  // The id of the turn STARTED most recently — set at send and never
  // cleared by a turn ending, which is what tells a late chunk from an
  // abandoned turn that something has happened since. `currentTurnRef`
  // cannot answer that: a buried turn releases the composer, so it is
  // not "current" either, and the two states need telling apart (a
  // buried turn with nothing after it may still move the screen; one
  // with a newer turn behind it may not). See `onMessage`.
  const latestTurnRef = useRef(null);
  // The token the CURRENT turn is trying to reverse, if any. `applyChunk`
  // reads it when the `undo` tool comes back — the chunk carries a tool
  // name, not a token.
  const pendingUndoRef = useRef(null);
  // The CURRENT place, read at send time rather than closed over: a
  // keeper can walk from rack 1 to rack 3 while a turn is composing, and
  // the turn that lands should carry where they are now.
  const placeRef = useRef(place);
  placeRef.current = place;
  // Item 10 — the keeper tapped × and no page has set a place since: the
  // request says "nowhere" (`{}`) so the server forgets with the screen.
  const nowhereRef = useRef(placeNowhere);
  nowhereRef.current = placeNowhere;
  // ── A STOPPED TURN REMEMBERS ONLY WHAT THE SCREEN SHOWED (Fable brief
  // 2026-10-23, item E) ────────────────────────────────────────────────
  //
  // The server finishes a stopped turn on its worker (a landed write keeps
  // its Undo) and used to remember the WHOLE reply — words the keeper never
  // saw, which the next turn then "corrected". Only this panel knows what
  // the bubble showed, so Stop remembers WHICH turn it stopped (its
  // one-time `turn_id`) and the NEXT request — typed, spoken or an Undo
  // press — reports the bubble's text AT THAT MOMENT (a chunk already in
  // flight lands in the bubble after Stop, and is then on screen too).
  // Only Stop sets it: an error-closed or buried turn is NEVER a stop.
  const turnIdsRef = useRef({});
  const stoppedTurnRef = useRef(null);
  const messagesRef = useRef([]);
  messagesRef.current = messages;
  /**
   * The report for the turn the keeper STOPPED, taken ONCE (the next
   * request carries it and it is gone), or null. ``{turn_id, shown_text,
   * nothing_shown}`` — the bubble's words plus its read-back row, as the
   * screen shows them. A bubble painted as an error is not a stop (N1).
   */
  const takeStoppedTurn = useCallback(() => {
    const stopped = stoppedTurnRef.current;
    stoppedTurnRef.current = null;
    if (!stopped || !stopped.turnId) return null;
    const bubble = messagesRef.current.find((m) => m.id === stopped.messageId);
    if (bubble && bubble.isError) return null;
    const parts = [bubble?.content || '', bubble?.readback || '']
      .map((part) => String(part).trim()).filter(Boolean);
    const shown = parts.join('\n\n');
    return { turn_id: stopped.turnId, shown_text: shown, nothing_shown: !shown };
  }, []);

  const addMessage = useCallback((messageObj) => {
    const message = {
      id: window.crypto?.randomUUID?.() || `m${Date.now()}${Math.random()}`,
      timestamp: new Date(),
      ...messageObj,
    };
    setMessages((prev) => [...prev, message]);
    return message;
  }, []);

  /**
   * Stand the silence timer down. Safe to call when there is none.
   *
   * With a `turnId` it clears the timer ONLY if that turn owns it — a
   * late `done` from an abandoned turn must not disarm the live one.
   * With none (Stop, New conversation, unmount) it clears whatever is
   * armed, because those three end every turn there is.
   */
  const clearWatchdog = useCallback((turnId) => {
    const live = watchdogRef.current;
    if (!live) return;
    if (turnId !== undefined && live.id !== turnId) return;
    clearTimeout(live.timer);
    watchdogRef.current = null;
  }, []);

  // #153: explicit New-conversation. The cid persists in sessionStorage
  // so reopening reattaches the SAME Redis session — the UI looked fresh
  // while server state (incl. any pending-action lock) survived.
  const handleNewConversation = useCallback(() => {
    if (activeStreamRef.current) {
      activeStreamRef.current.close?.();
      activeStreamRef.current = null;
    }
    // The turn this was watching belongs to a conversation that no
    // longer exists; its timer must not fire into the fresh one.
    //
    // ⚠️ AND A BURIED TURN'S ANSWER DIES HERE, DELIBERATELY (re-review,
    // item 3). The socket a stall left open is closed above, so a
    // read-back still in flight — and the Undo it carries — never
    // arrives. That is what "New conversation" MEANS: the keeper has
    // put the old one down, and a receipt appearing in a transcript
    // they have just cleared would be the surprise. The write itself is
    // untouched and is still reversible from the page.
    clearWatchdog();
    currentTurnRef.current = null;
    latestTurnRef.current = null;
    // A stop report belongs to the conversation it was made in (item E).
    stoppedTurnRef.current = null;
    const storageKey = froggerKeys.conversation(userId, facilityId);
    const freshCid = (window.crypto?.randomUUID?.() || `${Date.now()}`).slice(0, 16);
    try {
      // The old transcript belonged to the old conversation id. Drop it
      // with the id, so a "New conversation" leaves nothing behind for
      // a later mount to restore.
      sessionStorage.removeItem(logStorageKey(facilityId, conversationIdRef.current, userId));
      sessionStorage.setItem(storageKey, freshCid);
    } catch (e) { /* private mode */ }
    conversationIdRef.current = freshCid;
    restoredRef.current = null;
    // The turn the preview line was describing belonged to the old
    // conversation (review of batch 8, MINOR 3). Cleared HERE as well as
    // in the frame, because the id is what `noteAnswer` keys on and a
    // stale one would make the first part of the next turn look like a
    // continuation of a conversation that no longer exists.
    turnRef.current = { id: null, text: '', readback: '', counts: '' };
    setUndoneTokens(new Set());
    setActiveToolCall(null);
    setIsStreaming(false);
    sendingRef.current = false;
    setError(null);
    setMessages([{
      id: window.crypto?.randomUUID?.() || `m${Date.now()}`,
      timestamp: new Date(),
      type: 'system',
      content: 'New conversation started. Previous context has been cleared.',
    }]);
    inputRef.current?.focus();
    onNewConversation?.();
  }, [facilityId, onNewConversation, clearWatchdog, userId]);

  // ONE implementation, two frames — the same rule the panel itself
  // exists for. A frame calls `ref.current.newConversation()`; nothing
  // about the conversation id, the stored transcript or the stream lives
  // outside this file.
  React.useImperativeHandle(ref, () => ({
    newConversation: handleNewConversation,
  }), [handleNewConversation]);

  // Load summary stats — the welcome line only. A dock does not open
  // with a paragraph, so it does not ask for one.
  //
  // ⚠️ `await` INSIDE A try, never `.then()` on the return value. An api
  // wrapper that comes back `undefined` — a stubbed module, an export
  // that moved — makes `.then` a TypeError thrown out of an effect,
  // which takes the whole chat down over a decorative welcome line.
  // Awaiting `undefined` is just `undefined`, and the branch below
  // writes the same fallback a failed request does. (This is exactly
  // how the modal read before the panel was extracted; the `.then`
  // rewrite lost it, and the S3-06.2 read-back suite caught it.)
  useEffect(() => {
    if (!facilityId || !active || !showWelcome) return undefined;
    // A RESTORED TRANSCRIPT OUTRANKS THE WELCOME LINE. Both write
    // `setMessages([...])` wholesale, and the welcome lands second.
    if (restoredRef.current) return undefined;
    let cancelled = false;
    (async () => {
      let data = null;
      try {
        const res = await getAISummary(facilityId);
        data = res.data;
      } catch (err) { data = null; }
      if (cancelled) return;
      if (!data) {
        setMessages([{
          id: 'welcome',
          timestamp: new Date(),
          type: 'system',
          content: `FroggerAI ready for ${facilityName || 'Feeling Froggy'}.\n\nTools online.`,
        }]);
        return;
      }
      setMessages([{
        id: 'welcome',
        timestamp: new Date(),
        type: 'system',
        content: `FroggerAI ready for ${data.facility?.display_name || facilityName || 'Feeling Froggy'}.\n\n`
          + `${(data?.stats?.species ?? 0) || 0} species, ${(data?.stats?.clutches ?? 0) || 0} clutches, `
          + `${(data?.stats?.animals ?? 0) || 0} animals.\n\n`
          + 'Ask about the frog room, record what you have done, or say "what\'s due".',
      }]);
    })();
    return () => { cancelled = true; };
  }, [facilityId, active, showWelcome, facilityName]);

  // Auto-scroll to bottom
  useEffect(() => {
    scrollIntoViewMotionSafe(messagesEndRef.current);
  }, [messages]);

  // …and write it down. `streaming` is deliberately dropped: a reload
  // in the middle of a turn must not restore a bubble with a spinner
  // in it that nothing will ever finish.
  useEffect(() => {
    if (!facilityId) return;
    try {
      const keep = messages.slice(-LOG_KEEP).map((m) => ({
        ...m, streaming: false,
      }));
      sessionStorage.setItem(
        storageKeyFor(conversationIdRef.current),
        JSON.stringify({ messages: keep, undone: [...undoneTokens] }));
    } catch (e) { /* private mode, or the quota — the log is a nicety */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, undoneTokens, facilityId]);

  // Load voice config once the surface is actually in use. Same
  // await-in-a-try discipline as the summary above, and for the same
  // reason: the defaults are fine, and none of this is worth an
  // exception thrown out of an effect.
  useEffect(() => {
    if (!active) return;
    (async () => {
      try {
        const response = await getVoiceConfig();
        setVoiceConfig({
          maxDurationSeconds: response.data.max_duration_seconds,
          recommendedMaxSeconds: response.data.recommended_max_seconds,
          maxFileSizeMB: response.data.max_file_size_mb,
        });
      } catch (err) { /* defaults are fine */ }
    })();
  }, [active]);

  // Cleanup on unmount
  useEffect(() => () => {
    if (activeStreamRef.current) activeStreamRef.current.close?.();
    // A timer that outlives the component calls `setState` on nothing.
    if (watchdogRef.current) clearTimeout(watchdogRef.current.timer);
    watchdogRef.current = null;
    currentTurnRef.current = null;
  }, []);

  // ── ONE CHUNK HANDLER, TWO TRANSPORTS (S3-06.3) ────────────────────
  //
  // The streaming endpoint sends these as SSE events; the VOICE endpoint
  // is a plain POST and returns the same three things as FIELDS. Both go
  // through here, so a spoken feeding gets the same read-back and the
  // same Undo as a typed one, and "open Bruce" moves the screen whether
  // it was said or typed. A second copy of this for voice is how those
  // quietly stop being true.
  // THE NAVIGATE DISPATCH, OUT OF THE STREAM READER'S CALLBACK.
  //
  // See the `navigate` branch of `applyChunk` for why this exists. The
  // handle is kept so a frame that unmounts mid-turn does not move a
  // router that is on its way out.
  // ⚠️ NEVER A FRAME. A DISPATCH THAT MUST HAPPEN IS A TIMER (S3-06.4,
  // the Cowork re-sweep — `open <animal>` navigated NOWHERE on beta).
  //
  // The first cut handed this to `requestAnimationFrame`, which is the
  // right shape for something that only has to happen before the next
  // paint and the wrong one for something that has to happen AT ALL:
  // a browser does not run rAF callbacks in a page it is not painting.
  // Cowork drives beta through an IFRAME inside a wide tab, and the
  // navigate callback sat in the frame queue and was never called —
  // the tool ran, the model said "it's on your screen", the URL never
  // changed. Reproduced at 1440 and at 390, in an iframe, on two names.
  //
  // `setTimeout(…, 0)` is a TASK: throttled in a background tab, still
  // delivered. It keeps the whole reason the dispatch was deferred (the
  // route change must not run inside the stream reader's callback, or
  // WebKit dies on the landing's second history write) and drops the
  // only part that could silently not run.
  //
  // And the timer is not the only thing that fires it: the queued
  // target is FLUSHED on `done`, on completion and on stream close, so
  // a turn whose timer is starved still moves the screen before the
  // turn is over. Belt, braces, and a third thing — because a keeper
  // told the screen would move and left looking at the old one has no
  // way to tell that from a tool that did not run.
  const pendingNavigateRef = useRef(null);
  const navigateTimerRef = useRef(null);
  const flushNavigate = useCallback(() => {
    if (navigateTimerRef.current !== null) {
      clearTimeout(navigateTimerRef.current);
      navigateTimerRef.current = null;
    }
    const to = pendingNavigateRef.current;
    if (!to) return;
    // Cleared BEFORE the dispatch, so the flush on `done` and the timer
    // cannot both push the same route.
    pendingNavigateRef.current = null;
    onNavigate?.(to);
  }, [onNavigate]);
  // ARM — or RE-ARM — the dispatch as a MACROTASK, never as a call.
  //
  // ⚠️ `done` IS NOT A DIFFERENT TICK FROM `navigate` (S3-06.4 review,
  // batch 4). The SSE reader decodes whatever bytes one `reader.read()`
  // handed it and calls this callback once per event in a single
  // synchronous loop, so `navigate` and `done` routinely arrive in the
  // SAME tick. Calling `flushNavigate()` straight from the `done` arm
  // therefore put the router push back INSIDE the reader's callback —
  // exactly the thing the deferral exists to prevent, with the landing's
  // own `setSearchParams` history write in the same tick, which is what
  // killed WebKit. So `done` and the stream's completion RE-ARM the
  // timer instead: still the belt-and-braces flush for a starved rAF,
  // still out of the reader.
  //
  // The one direct call that survives is the VOICE `finally`, which is
  // not a reader callback at all — it is an ordinary `await` resumption.
  const armNavigate = useCallback(() => {
    if (!pendingNavigateRef.current) return;
    if (navigateTimerRef.current !== null) clearTimeout(navigateTimerRef.current);
    navigateTimerRef.current = setTimeout(() => {
      navigateTimerRef.current = null;
      flushNavigate();
    }, 0);
  }, [flushNavigate]);
  const queueNavigate = useCallback((to) => {
    // ONE per turn is the contract, but a target that has not been
    // dispatched yet is REPLACED rather than queued twice — the LAST
    // one wins, the same rule the backend applies when two navigate
    // tools ran.
    pendingNavigateRef.current = to;
    armNavigate();
  }, [armNavigate]);
  // A TURN DOES NOT INHERIT THE PREVIOUS TURN'S DESTINATION. A navigate
  // that was queued and then never dispatched — the turn errored, the
  // stream died — would otherwise sit in the ref and fire on the NEXT
  // turn's `done`, moving the screen somewhere nobody asked for this
  // time. Every turn starts by dropping it, and an errored turn drops
  // its own.
  const resetNavigate = useCallback(() => {
    pendingNavigateRef.current = null;
    if (navigateTimerRef.current === null) return;
    clearTimeout(navigateTimerRef.current);
    navigateTimerRef.current = null;
  }, []);
  useEffect(() => () => {
    // Unmounting mid-turn: drop the pending push rather than moving a
    // router that is on its way out.
    pendingNavigateRef.current = null;
    if (navigateTimerRef.current === null) return;
    clearTimeout(navigateTimerRef.current);
    navigateTimerRef.current = null;
  }, []);

  // ── A (batch 8): TELLING THE FRAME AN ANSWER HAPPENED ──────────────
  //
  // The panel knows a turn produced something; only the FRAME knows what
  // to do about it (the bottom dock puts itself up, the desktop panel is
  // already on screen). So this reports two different facts, and they
  // are deliberately separate:
  //
  //   `onAnswer()`  — ONCE per turn, at the FIRST sign of an answer.
  //                   Not at completion: the navigate collapse runs on a
  //                   timer after `done`, and an expand at completion
  //                   would race it and undo it.
  //   `onPreview()` — the line a COLLAPSED frame shows. The read-back
  //                   outranks the prose, because the read-back is the
  //                   receipt and it carries the Undo; the round's
  //                   counts are the last resort, for a turn whose only
  //                   output was numbers.
  //
  // Held in refs so a frame that re-creates its callbacks does not
  // re-create `applyChunk` and, through it, `sendUtterance`.
  const onAnswerRef = useRef(onAnswer);
  onAnswerRef.current = onAnswer;
  const onPreviewRef = useRef(onPreview);
  onPreviewRef.current = onPreview;
  const turnRef = useRef({ id: null, text: '', readback: '', counts: '' });
  // Per message: whether an iteration boundary (a tool call or result)
  // sat since the last text fragment, and the tool_call ids announced
  // and not yet answered, per tool name. See `assembleChunks` /
  // `addToolChip` (Cowork run 25, F2 / F8).
  const assemblyRef = useRef(new Map());
  const assemblyFor = useCallback((messageId) => {
    let entry = assemblyRef.current.get(messageId);
    if (!entry) {
      entry = { boundary: false, pendingIds: {}, tools: [] };
      assemblyRef.current.set(messageId, entry);
      if (assemblyRef.current.size > 50) {
        assemblyRef.current.delete(assemblyRef.current.keys().next().value);
      }
    }
    return entry;
  }, []);
  const noteAnswer = useCallback((messageId, part) => {
    const turn = turnRef.current;
    if (turn.id !== messageId) {
      turnRef.current = {
        id: messageId, text: '', readback: '', counts: '', announced: false,
        // A turn is quiet until something loud arrives in it.
        quiet: true,
      };
    }
    const next = turnRef.current;
    // ⚠️ AND THE PREVIEW IS SUPERSEDED TOO (run 7, C1). The collapsed
    // dock shows the turn's first line; on a superseded turn that line
    // is the promise nothing kept, so the server's question REPLACES
    // it rather than being appended behind it.
    if (part.text && part.replaces) next.text = part.text;
    else if (part.text) next.text = assembleChunks(next.text, part.text, part.boundary);
    if (part.readback) next.readback = part.readback;
    if (part.counts) next.counts = part.counts;
    // ⚠️ A FAILURE IS AN ANSWER (review of batch 8, MAJOR 2). It was the
    // one outcome that got neither the expand nor the preview, because
    // the three error paths wrote the bubble directly and never came
    // through here — so a 503 on a phone left a collapsed row saying
    // nothing, on the turn that most needed the keeper's attention. It
    // is deliberately NOT appended to `next.text`: an error replaces the
    // turn's content, it does not continue it.
    if (part.error) next.text = part.error;
    // ── QUIET (review of batch 8, MAJOR 1) ───────────────────────────
    //
    // `route`'s short-circuits — "stop", a bare "no", "next", the Undo
    // press — emit an ordinary `chunk`, and expanding the phone dock on
    // those is the dock answering "leave me alone" by covering the
    // screen (and `toggle(true)` PERSISTS). The server flags them
    // `quiet`; here that means NO EXPAND and NO UNREAD DOT. The preview
    // line is still published, because a collapsed dock has nowhere
    // else to say the sentence.
    //
    // Only a part that SPEAKS can make a turn loud: `flush` carries no
    // words and must not undo the quiet at the end of the turn.
    const speaks = !!(part.text || part.readback || part.counts || part.error);
    if (speaks && !part.quiet) next.quiet = false;
    const first = !next.announced;
    if (first && !next.quiet) {
      next.announced = true;
      onAnswerRef.current?.();
    }
    // ⚠️ THE PREVIEW IS NOT STREAMED. Publishing it on every delta means
    // a `setState` in the FRAME per token, which re-renders the frame
    // AND this panel under it — twice the work per chunk, on the one
    // device the round is walked with. It is published on the events
    // that are worth a repaint: the FIRST sign of an answer (so a
    // collapsed dock has a line straight away), a read-back or a counts
    // block (each arrives once), and the end of the turn.
    if (first || part.readback || part.counts || part.error || part.flush) {
      onPreviewRef.current?.(next.readback || next.text || next.counts,
        { quiet: next.quiet });
    }
  }, []);

  /** The turn is over: publish the line it ended with. */
  const flushPreview = useCallback((messageId) => {
    if (turnRef.current.id !== messageId) return;
    noteAnswer(messageId, { flush: true });
  }, [noteAnswer]);

  /**
   * @param {object} [opts]
   * @param {boolean} [opts.announce=true] — whether this chunk may tell
   *   the FRAME anything (`onAnswer`'s expand, the collapsed preview,
   *   the unread dot) **and the shared tool spinner**, which is one
   *   slot for the whole panel. False for a turn a NEWER one has
   *   superseded: it still writes its own bubble, its receipt and its
   *   Undo, but the frame's one "latest answer" line — and the
   *   spinner the LIVE turn is filling — belong to that live turn. See
   *   `onMessage` (re-review 3, item 1).
   */
  const applyChunk = useCallback((messageId, data, opts) => {
    if (!data) return;
    const announce = opts?.announce !== false;
    if (data.type === 'delta' || data.type === 'text' || data.type === 'chunk') {
      const textContent = data.text || data.content || '';
      if (textContent) {
        // ⚠️ **THE SERVER'S QUESTION REPLACES THE MODEL'S PROMISE**
        // (Cowork run 6 A3, the panel half — Cowork run 7, C1). The
        // model narrates BEFORE it calls ("I'll record that now."), the
        // check then refuses the write, and both sentences were on
        // screen: two questions for one turn, the dead one first and
        // reading like the answer. `narration_superseded` is the
        // server saying "what came before this is a promise nothing
        // kept". The narration is DROPPED — not muted — because the
        // keeper has to answer the question, and a greyed sentence
        // above it is one more thing to read past. The server sets the
        // flag only when there IS a narration to supersede (run 6
        // round 2, S1/V1), so this can never blank the only answer.
        const supersedes = !!data.narration_superseded;
        // A fragment continues the narration it follows; only a tool
        // call/result between them, or a sentence the SERVER wrote,
        // starts a new paragraph (run 25, F2 — see `assembleChunks`).
        const assembly = assemblyFor(messageId);
        // A whitespace-only fragment does not spend the boundary: the
        // next fragment with words in it is where the paragraph starts.
        const speaks = !!textContent.trim();
        const boundary = (assembly.boundary && speaks) || !!data.synthesized;
        if (speaks) assembly.boundary = false;
        if (announce) {
          noteAnswer(messageId, {
            text: textContent, quiet: !!data.quiet, replaces: supersedes,
            boundary,
          });
        }
        setMessages((prev) => prev.map((m) => (
          m.id === messageId
            ? {
              ...m,
              content: supersedes
                ? textContent
                : assembleChunks(m.content, textContent, boundary),
              supersededNarration: supersedes
                ? (m.supersededNarration || m.content || '')
                : m.supersededNarration,
            }
            : m)));
      }
    } else if (data.type === 'boundary') {
      // A NEW MODEL RESPONSE (Cowork run 25, closure item 4): the server
      // opens every response after the first with this marker, because a
      // `tool_search` or a `pause_turn` loop-back emits no chunk of its
      // own. Exactly what a tool call between two narrations does — the
      // next fragment with words in it starts a new paragraph.
      assemblyFor(messageId).boundary = true;
    } else if (data.type === 'tool_call') {
      setActiveToolCall(data.name);
      const assembly = assemblyFor(messageId);
      assembly.boundary = true;
      if (data.name && !assembly.tools.includes(data.name)) assembly.tools.push(data.name);
      if (data.id && data.name) {
        if (!assembly.pendingIds[data.name]) assembly.pendingIds[data.name] = [];
        assembly.pendingIds[data.name].push(String(data.id));
      }
    } else if (data.type === 'tool_result') {
      if (announce) setActiveToolCall(null);
      const assembly = assemblyFor(messageId);
      assembly.boundary = true;
      const resultId = data.id
        || (assembly.pendingIds[data.name] || []).shift() || undefined;
      // G — AN UNDO THAT LANDED. The reversal is an ordinary turn, so
      // the only thing that says it worked is the `undo` tool coming
      // back successful. Marked HERE and not on the turn's completion,
      // because a turn where the model answered "there is nothing to
      // undo" also completes, and a button that goes grey on that is
      // lying in the other direction.
      if (data.name === 'undo' && data.success !== false
          && pendingUndoRef.current) {
        const spent = pendingUndoRef.current;
        pendingUndoRef.current = null;
        setUndoneTokens((prev) => new Set([...prev, spent]));
      }
      // AN UNDO THAT LANDED CHANGED THE PAGE UNDER THE DOCK (Cowork QA
      // 2026-10-21): the open page reads again (`utils/froggerWrites`).
      if (data.name === 'undo' && data.success !== false) {
        announceWrite({ tools: ['undo'] });
      }
      // The tool names ARE the receipt for a read turn — the harness and
      // the operator both look for them.
      //
      // ⚠️ A CALL THAT ASKED A QUESTION WROTE NOTHING (re-sweep #4).
      // `createAnimal` stops before the write to offer "Create X as a Y
      // in 1B? Say yes", and this row drew the same chip it draws for a
      // create that really landed — the receipt claiming a write on the
      // turn whose whole point was not writing yet. The server marks
      // those `asked` and the chip below renders muted and says so.
      // Stored as an OBJECT so the flag survives the reload that the
      // transcript now survives; `toolChip` reads the old bare-string
      // shape too, because transcripts written before this are already
      // in people's browsers.
      // ⚠️ A SERVER QUESTION DRAWS NO CHIP (Cowork run 38, H2c). When
      // the tool stopped to ASK — "which rack is 1A on?" — the question
      // itself is the answer on screen, and a grey dashed `getEnclosure?`
      // under it said the same thing again in a tool's name. `question`
      // is set by the server for a clarification only; a confirmation
      // OFFER ("Create X? Say yes") keeps its muted chip.
      if (data.question) return;
      setMessages((prev) => prev.map((m) => (
        m.id === messageId
          ? {
            ...m,
            tools: addToolChip(m.tools,
              { name: data.name, asked: !!data.asked, id: resultId }),
          }
          : m)));
    } else if (data.type === 'readback') {
      // The RECEIPT for a turn that wrote (S3-06.2). Its own quieter row
      // under the bubble, with an Undo per write.
      // ⚠️ TOKENS COUNT EVEN WITH NO SENTENCE. A read-back that is all
      // Undo and no prose is still the one thing on the screen the
      // keeper may need in the next ten seconds, so it opens the dock
      // like any other answer; `noteAnswer` ignores an empty preview.
      if (announce && (data.text || (data.undo_tokens || []).length)) {
        noteAnswer(messageId, { readback: data.text || '' });
      }
      setMessages((prev) => prev.map((m) => (
        m.id === messageId
          ? { ...m, readback: data.text || '', undoTokens: data.undo_tokens || [] }
          : m)));
      // THE TURN MAY HAVE WRITTEN, so the page under the dock may be
      // stale: Foods / Tasks Today read again (Cowork QA 2026-10-21). A
      // read-back means the turn called a WRITE tool — usually one that
      // wrote, but it can also be one that wrote nothing (a refusal, a
      // question); a refetch on such a turn is a harmless re-read. A
      // superseded turn's write is still a write — this is not gated on
      // `announce`.
      if (data.text || (data.undo_tokens || []).length) {
        announceWrite({ tools: [...assemblyFor(messageId).tools] });
      }
    } else if (data.type === 'counts') {
      // THE ROUND'S BUCKETS, from the tool result (re-sweep item 3).
      // Stored whole minus the chunk's own `type`, so `counts.text` —
      // the server's line — is what the row prefers and the numbers are
      // there for the one fallback. A turn that called neither
      // `whatsDue` nor `startWalk` emits no chunk and gets no row.
      const { type: _chunkType, ...counts } = data;
      // G (batch 8) — this chunk now arrives from `execute_tools`, as
      // soon as the tool RESULT does, rather than from `readback` after
      // the model has finished writing. It is the first thing on screen
      // on a "walk me through it", which used to be 17 s of empty panel.
      if (announce) noteAnswer(messageId, { counts: countsLine(counts) });
      setMessages((prev) => prev.map((m) => (
        m.id === messageId ? { ...m, counts } : m)));
    } else if (data.type === 'navigate') {
      // S3-06.3 — the screen moves. ONE per turn (the backend keeps the
      // last navigate tool's target), and the frame decides what "open
      // rack 3" means for the router it lives in.
      console.info('[FroggerAI] navigate:', data.to);
      setMessages((prev) => prev.map((m) => (
        m.id === messageId ? { ...m, navigate: data.to } : m)));
      // ⚠️ NOT FROM INSIDE THE STREAM READER (S3-06.4, found in WebKit).
      //
      // This callback runs on an SSE chunk, with the connection still
      // open and `done` still to come. Calling `onNavigate` here made
      // react-router swap the WHOLE route — unmount the racks board,
      // mount the Animals grid, run its effects — synchronously inside
      // the reader's callback, and the landing's own `history` write
      // (`AnimalsGrid` consuming `?animal=` with `setSearchParams(...,
      // {replace: true})`) then landed in the SAME tick as the router's
      // push. Chromium tolerates it; WebKit does not — the whole engine
      // went down on the landing, every time, on both iPad portrait
      // descriptors and on the 834/1194 one.
      //
      // So the dispatch is HANDED to the next frame. The stream reader
      // returns first, React commits this message on its own, and the
      // route change is a task of its own with the two history writes in
      // separate ticks. A frame is ~16ms — not a delay anybody sees, and
      // the alternative was a landing that killed the tab.
      if (data.to) queueNavigate(data.to);
    } else if (data.type === 'error') {
      // MAJOR 2 — THE ONE TURN THAT MOST NEEDS TELLING. Every failure
      // now lands here instead of writing the bubble by hand, so a
      // failed turn expands the dock and leaves a preview exactly like a
      // successful one. `isError` is what paints the bubble red.
      if (announce) {
        setActiveToolCall(null);
        noteAnswer(messageId, { error: ERROR_PREVIEW });
      }
      // `append`: something had already arrived — the text stays and
      // the error goes UNDER it (`DROPPED_TEXT`), never instead of it.
      setMessages((prev) => prev.map((m) => (
        m.id === messageId
          ? {
            ...m,
            content: (data.append && m.content)
              ? `${m.content}\n\n${data.text || ERROR_TEXT}`
              : (data.text || ERROR_TEXT),
            streaming: false,
            isError: true,
          }
          : m)));
    } else if (data.type === 'start') {
      // ⚠️ THE SERVER SAYS THE CONVERSATION IS GONE. `session: 'new'`
      // means the backend has no memory of this conversation id — the
      // Redis session expired, or was flushed. A transcript restored
      // from storage is then describing a conversation nobody else
      // remembers, so it is dropped rather than left on screen looking
      // live. Only the RESTORED part goes: whatever is happening right
      // now is this turn's, and it stays.
      if (data.session === 'new' && restoredRef.current) {
        restoredRef.current = null;
        setMessages((prev) => prev.filter((m) => !m.restored));
      }
    } else if (data.type === 'done') {
      setActiveToolCall(null);
      // The turn's final line, for a dock that is (or is about to be)
      // collapsed. See `noteAnswer`: the preview is not streamed.
      flushPreview(messageId);
      // THE TURN IS OVER AND THE SCREEN HAS NOT MOVED. If the queued
      // navigate's timer has not run — a throttled tab, a hidden
      // iframe, a browser that has stopped scheduling for this document
      // — this re-arms it so it cannot be lost with the turn.
      //
      // RE-ARMED, NOT CALLED: this arm runs inside the stream reader,
      // and `navigate` + `done` arrive in one `reader.read()`.
      armNavigate();
    }
  }, [queueNavigate, armNavigate, noteAnswer, flushPreview, assemblyFor]);

  // I-03 R6 / S3-06.2 — ONE path for "send this utterance", because the
  // Undo button is not a different kind of message, and neither is a
  // voice segment or "Walk me through it".
  //
  // `display` (S3-06.4) is what the keeper's own bubble SAYS when it is
  // not what goes on the wire. Cowork QA of R6: pressing Undo put
  // `undo u0739a2b4c1` in the transcript as if the keeper had typed a
  // hex string. The token is machine addressing; the bubble reads
  // "Undo fed rack QADELETE AI Rack", which is what the button said.
  const sendUtterance = useCallback(async (msg, { display } = {}) => {
    // SYNCHRONOUS GUARD (the S3-03.2 rule: a state flag is not a guard).
    // `isStreaming` is React state and does not update until the next
    // render, so two taps inside one tick — an Enter double-submit, or a
    // double-tap on Undo with wet hands — both read `false` and both
    // send. On an Undo that is two reversals of one token; on a record
    // it is two feedings.
    if (!msg || sendingRef.current || isStreaming || !canSend) {
      if (!canSend) {
        setError('No facility. Open a facility page and try again.');
      }
      return;
    }
    sendingRef.current = true;

    const authToken = localStorage.getItem('authToken');
    if (!authToken) {
      sendingRef.current = false;
      setError('Authentication required. Please log in again.');
      return;
    }

    if (activeStreamRef.current) {
      activeStreamRef.current.close?.();
      activeStreamRef.current = null;
    }

    setIsStreaming(true);
    setError(null);
    // A NEW TURN OWNS THE SCREEN. Anything the last one queued and never
    // pushed dies here rather than landing on this turn's `done`.
    resetNavigate();
    addMessage({ type: 'user', content: display || msg });
    const aiMessage = addMessage({ type: 'assistant', content: '', streaming: true });
    // THIS turn owns the composer until it ends. See `watchdogRef`.
    currentTurnRef.current = aiMessage.id;
    latestTurnRef.current = aiMessage.id;
    // ONE TURN, ONE RUN — this turn's one-time id, remembered against its
    // bubble so a Stop can name it (item E). The report for a turn stopped
    // BEFORE this one rides on this request, taken once.
    const turnId = newTurnId();
    turnIdsRef.current[aiMessage.id] = turnId;
    const stoppedTurn = takeStoppedTurn();

    // ── A STREAM THAT NEVER ANSWERS MUST SAY SO (run 13, F1) ─────────
    //
    // Everything from here to `feedWatchdog()` is declared OUTSIDE the
    // `try` on purpose: the `catch` below needs `revive` and
    // `releaseComposer` too, and a turn that fails to start is a turn
    // like any other.
    //
    // The handle is taken SYNCHRONOUSLY: `askFroggerStream`'s promise
    // does not settle until the stream is over, so waiting for it
    // leaves nothing to CLOSE a stalled turn's socket with when the
    // keeper finally does put it down.
    const handleRef = { current: null };
    /** Is this turn still the one the composer is waiting on? */
    const isCurrent = () => currentTurnRef.current === aiMessage.id;
    /**
     * Has a NEWER turn been started since this one?
     *
     * ⚠️ NOT `!isCurrent()`. A buried turn releases the composer, so it
     * is not "current" either — and with nothing behind it, its late
     * `navigate` is still the answer to the only question anybody
     * asked, and must still move the screen (re-review 2, item 1's
     * revive case). `latestTurnRef` is set at send and never cleared by
     * a turn ENDING, so it is the one fact that tells "abandoned" from
     * "superseded".
     */
    const supersededByANewerTurn = () => latestTurnRef.current !== aiMessage.id;
    /**
     * Hand the composer back — and ONLY from the turn that holds it
     * (review, item 4). A late completion from an abandoned turn
     * calling this would clear the LIVE turn's send guard, which is a
     * double-send with a stranger's timing.
     *
     * ⚠️ IT DOES NOT TOUCH `activeStreamRef` (re-review, item 1). The
     * composer coming back and the socket being finished are two
     * different facts: a BURIED turn gives the composer back while its
     * stream is still open, precisely so a late answer can still
     * arrive. `endStream` is the other half.
     */
    const releaseComposer = () => {
      if (!isCurrent()) return;
      currentTurnRef.current = null;
      sendingRef.current = false;
      setIsStreaming(false);
    };
    /**
     * This turn's stream is OVER — the reader returned, `[DONE]`, an
     * error. Forget the handle, but only if it is still the app's: a
     * late completion from an abandoned turn must not drop the LIVE
     * turn's socket out of the slot every close reads.
     */
    const endStream = () => {
      if (activeStreamRef.current === handleRef.current) {
        activeStreamRef.current = null;
      }
    };
    /** #161: a closed turn never leaves an EMPTY assistant bubble. */
    const closeBubble = (usage) => {
      setMessages((prev) => prev.map((m) => (
        m.id === aiMessage.id
          ? {
            ...m,
            content: m.content || SILENT_TURN_TEXT,
            streaming: false,
            isError: false,
            completedAt: new Date(),
            usage: usage === undefined ? m.usage : usage,
          }
          : m)));
    };

    // Buried RIGHT NOW, and buried at any point in this turn.
    let buried = false;
    let everBuried = false;
    // Did ANYTHING of the answer arrive (any chunk but a ping or the
    // `start` frame)? Decides whether a dropped stream's line goes under
    // the text that came (`DROPPED_TEXT`) or stands alone
    // (`NO_ANSWER_TEXT`).
    let heardSomething = false;

    /**
     * THE BURIAL IS PROVISIONAL (review, item 1).
     *
     * The first cut ignored every chunk that arrived after a stall,
     * on the argument that a bubble the keeper has been told is dead
     * must not start talking again. That is backwards: the late chunk
     * is the EVIDENCE the stall line says to go and look for. A turn
     * that stalls between its `tool_result` and its `readback` has
     * already written the row, and dropping the read-back drops its
     * UNDO TOKENS — the one control that takes the write back.
     *
     * So a late chunk RAISES the turn: the line goes, the real answer
     * replaces it, its Undo buttons render. The composer is NOT taken
     * back (the keeper has had it for two minutes and may be typing),
     * and the turn gets no new watchdog, because nothing is waiting
     * on it any more.
     */
    // ⚠️ AND IT DOES NOT PUT THE SPINNER BACK. `streaming` stays false:
    // the composer has been handed back, so nothing in the app is
    // waiting on this turn, and the READ-BACK ROW — the one that
    // carries the Undo buttons — is deliberately gated on
    // `!message.streaming`. A revived turn that claimed to be streaming
    // again would render its answer and HIDE the control that takes the
    // write back, which is the whole reason the late chunk matters.
    // ⚠️ **THE BURIED LINE IS RECOGNISED BY ITS HEAD, NOT BY EQUALITY**
    // (re-review 3, item 2). An error after a burial APPENDS its own
    // sentence under the evidential line, so the content is no longer
    // equal to the constant — and a `readback` arriving after that
    // error must still replace BOTH with the real answer and its Undo.
    // One reading, used by `revive` and by `errorAfterBurial`.
    const buriedLineStands = (content) => (
      String(content || '').startsWith(NO_ANSWER_TEXT));

    const revive = () => {
      if (!buried) return;
      buried = false;
      setMessages((prev) => prev.map((m) => (
        m.id === aiMessage.id && buriedLineStands(m.content)
          ? { ...m, content: '', isError: false, streaming: false }
          : m)));
      // The preview was the stall line; the real answer replaces it.
      if (turnRef.current.id === aiMessage.id) turnRef.current.text = '';
    };

    /**
     * ⚠️ **THE WATCHDOG STOPS WAITING. IT NEVER STOPS THE TURN**
     * (re-review, item 1).
     *
     * The first cut ABORTED the socket here, which makes the burial
     * final: after the abort no chunk can ever arrive, so `revive` has
     * nothing to revive with and the turn's Undo — for a write that may
     * well have landed — is unreachable for good. That is the opposite
     * of what the evidential line promises. The window is our patience
     * running out, not a verdict about the server, and a turn buried by
     * mistake (a legitimate silence past 120 s) must still be able to
     * come back.
     *
     * So the burial is EXACTLY: say the line, release the composer, do
     * the per-turn bookkeeping. **The socket is left open.** Closing one
     * is somebody's decision, and there are four of them — Stop, "New
     * conversation", the NEXT turn's own bookkeeping (which closes
     * whatever is in `activeStreamRef` before it starts), and unmount —
     * plus the server ending the stream itself.
     */
    const stall = () => {
      clearWatchdog(aiMessage.id);
      // Only the turn that owns the composer can be buried: an
      // abandoned one has nobody waiting on it (review, item 4).
      if (!isCurrent()) return;
      buried = true;
      everBuried = true;
      console.warn(`[FroggerAI] no chunk in ${STREAM_SILENCE_MS}ms — turn abandoned (socket left open)`);
      setActiveToolCall(null);
      // Nothing this turn queued is news any more.
      resetNavigate();
      releaseComposer();
      applyChunk(aiMessage.id, { type: 'error', text: NO_ANSWER_TEXT });
    };
    /**
     * ⚠️ **AN ERROR AFTER A BURIAL IS APPENDED, NEVER A REPLACEMENT**
     * (re-review, item 2 — reproduced). A stalled socket usually ends
     * in an error eventually, and that error REPLACED the evidential
     * line with "Error: stream failed" — which is a true sentence about
     * the transport and drops the only thing the keeper needed, that
     * the write may have landed and the page is where to check. The
     * transport's reason is the smaller fact and goes underneath.
     */
    // ⚠️ **AND THE TURN STAYS BURIED** (re-review 3, item 2 —
    // reproduced). Clearing `buried` here made the error the end of the
    // turn: a `readback` arriving afterwards was APPENDED beneath it
    // rather than replacing the pair, so the keeper read the stall
    // line, then a transport error, then the real answer — three
    // sentences for one turn, with the Undo hanging off the last. A
    // burial is PROVISIONAL and an error does not settle it; the line
    // (and now the error under it) is still revivable, and a later
    // `readback` replaces both with the answer and its Undo.
    const errorAfterBurial = (text) => {
      setMessages((prev) => prev.map((m) => (
        m.id === aiMessage.id && buriedLineStands(m.content)
          ? {
            ...m,
            content: `${m.content}\n\n${text}`,
            isError: true,
            streaming: false,
          }
          : m)));
    };
    // ANY CHUNK IS LIFE. A slow but live stream is not a stall, so the
    // timer starts again from every chunk of every kind — and only
    // ever for the turn the composer is waiting on.
    const feedWatchdog = () => {
      if (!isCurrent()) return;
      clearWatchdog();
      watchdogRef.current = {
        id: aiMessage.id,
        timer: setTimeout(stall, STREAM_SILENCE_MS),
      };
    };
    feedWatchdog();

    try {
      const onMessage = (data) => {
        // ── A KEEPALIVE IS NOT AN ANSWER (review, item 2) ───────────
        //
        // Our chunks leave the graph at NODE boundaries and the model
        // client retries with sleeps, so a live turn can genuinely say
        // nothing for minutes. The server emits `{"type":"ping"}` every
        // 15 s while a turn is running; it FEEDS the watchdog and
        // renders NOTHING — no bubble, no preview, no unread dot —
        // which is why it returns before `applyChunk` rather than
        // relying on that function to ignore it. A ping arriving on an
        // abandoned turn feeds nothing, because `feedWatchdog` is
        // current-only.
        if (data?.type === 'ping') { feedWatchdog(); return; }
        if (data?.type !== 'start') heardSomething = true;
        // An error is the one late chunk that must not REPLACE the
        // evidential line. See `errorAfterBurial`.
        if (data?.type === 'error' && buried) {
          clearWatchdog(aiMessage.id);
          errorAfterBurial(data.text || ERROR_TEXT);
          return;
        }
        // ── THE BURIED TURN MAY SPEAK FOR ITSELF, NEVER FOR THE
        //    SCREEN (re-review 3, item 1 — reproduced, a wrong move) ──
        //
        // Turn 1 stalls and is buried; the keeper sends turn 2; turn
        // 1's socket — LEFT OPEN by the watchdog, deliberately — then
        // delivers its `navigate`, and `onNavigate` moved the page
        // while turn 2 was streaming. That route mounts the board,
        // `usePlaceSetter` sets the place, and the dock's chip is what
        // every write gate reads: a turn nobody is waiting on re-points
        // the target of the next spoken feeding.
        //
        // So once a NEWER turn exists this one still writes its OWN
        // bubble — the deltas, the receipt and its Undo, which is the
        // whole reason the socket was left open — and says nothing to
        // the SCREEN or to the frame: no `navigate`, no `tool_call`
        // spinner, and no `noteAnswer` (`announce: false`), which is
        // the expand, the collapsed preview and the unread dot. Its
        // `done` closes its own bubble and nothing else.
        const superseded = supersededByANewerTurn();
        if (superseded && (data?.type === 'navigate' || data?.type === 'tool_call')) return;
        // A late chunk is the evidence, not noise. See `revive`.
        revive();
        if (data?.type === 'done') clearWatchdog(aiMessage.id);
        else feedWatchdog();
        if (superseded && data?.type === 'done') {
          // `applyChunk`'s `done` arm flushes the preview and ARMS the
          // queued navigate; neither belongs to a turn behind the live
          // one. Its own bubble still closes.
          if (everBuried) closeBubble();
          return;
        }
        applyChunk(aiMessage.id, data, { announce: !superseded });
        // A `done` for a turn the composer has already let go closes
        // the bubble itself: its `onComplete` may never come.
        if (data?.type === 'done' && everBuried) closeBubble();
      };

      const onError = (err) => {
        clearWatchdog(aiMessage.id);
        const mine = isCurrent();
        console.warn('[FroggerAI] stream error:', err?.message);
        // The stream really is over now, whatever else is true.
        endStream();
        releaseComposer();
        // The turn failed, so its destination is not news any more — and
        // left queued it would fire on the NEXT turn's `done`. Only the
        // current turn may drop it: the live turn's target is not this
        // one's to throw away.
        if (mine) resetNavigate();
        // ⚠️ **EVERY BRANCH CLOSES THE BUBBLE** (review of 9ca56cb3,
        // item 1). The 401 branch used to set the banner and RETURN, which
        // left the turn's bubble on "Streaming…" for good — and it was
        // matched on the words "Authentication failed", so a 403 (a
        // permission refusal, token perfectly good) logged the keeper out.
        // The STATUS decides now, through `streamFailureText`.
        //
        // ⚠️ AND A TRANSPORT FAILURE IS NEVER RE-SENT (the review of the
        // FroggyAI polish batch). The service no longer retries this turn
        // or re-asks it as a JSON POST — the server keeps running a turn
        // after the client goes, so a re-send is a second feeding. What
        // this side KNOWS is that the answer did not finish; whether the
        // request landed it cannot know, and the line says so.
        const { text, append, signOut } = streamFailureText(err, { heardSomething });
        if (signOut) {
          localStorage.removeItem('authToken');
          setError(SIGNED_OUT_TEXT);
        }
        // A buried turn keeps its evidential line; the reason goes under.
        if (buried) {
          errorAfterBurial(err?.transport ? `Error: ${err?.message || 'stream failed'}` : text);
          return;
        }
        revive();
        // THROUGH `applyChunk`, not around it (review, MAJOR 2): the
        // expand and the preview line live in the chunk handler, and a
        // path that writes the bubble itself is a turn the dock never
        // hears about.
        applyChunk(aiMessage.id, { type: 'error', text, append });
      };

      const onComplete = (data) => {
        clearWatchdog(aiMessage.id);
        // A stream that finishes after a burial is the answer arriving
        // late, not noise: it raises the turn like any other chunk.
        revive();
        closeBubble(data?.usage);
        endStream();
        releaseComposer();
        // …and the preview, for the stream that closed without a `done`.
        // Not for a SUPERSEDED turn: the frame's one preview line is the
        // live turn's, and `turnRef` may not have moved to it yet if it
        // has said nothing so far (re-review 3, item 1).
        if (!supersededByANewerTurn()) flushPreview(aiMessage.id);
        // …AND IF `done` NEVER ARRIVED. The stream can close on its own
        // (the reader returns, `[DONE]`, an EventSource completion)
        // without the `done` chunk this panel keys off. A navigate
        // queued earlier in the turn must not be lost to that — and
        // this completion can be reached from inside the reader too, so
        // it re-arms rather than pushing.
        armNavigate();
      };

      await streamingAPI.askFroggerStream(
        facilityId,
        msg,
        authToken,
        onMessage,
        onError,
        onComplete,
        conversationIdRef.current,
        // S3-06.3 — WHERE THE KEEPER IS, on every turn. Ids only; the
        // backend resolves them against the facility and takes the NAME
        // from its own row (`langgraph/views._resolve_place`).
        // Item 10: `{}` after the keeper tapped × ("nowhere").
        placeForWire(placeRef.current, nowhereRef.current),
        // ⚠️ THE APP'S ACTIVE STREAM IS SET **HERE** (review, item 3).
        // It used to be assigned from this call's return value, which
        // does not settle until the stream is OVER — so Stop, "New
        // conversation" and the unmount cleanup all closed `null` and
        // left the socket open. The handle arrives before the first
        // byte; `handleRef` is this turn's own copy (the stall closes
        // it even after the turn stops being current) and
        // `activeStreamRef` is the app's, which only the current turn
        // may write.
        (handle) => {
          handleRef.current = handle;
          if (isCurrent()) activeStreamRef.current = handle;
        },
        // ⚠️ ONE TURN, ONE RUN — this turn's one-time id, minted ONCE
        // above, the one send path every turn takes (typed, the Undo
        // press, a queued `initialUtterance`). The server claims it
        // before running anything (`langgraph/turn_claim.py`).
        turnId,
        // Item E — what the screen showed of the turn the keeper stopped.
        stoppedTurn,
      );
    } catch (err) {
      clearWatchdog(aiMessage.id);
      revive();
      endStream();
      releaseComposer();
      applyChunk(aiMessage.id, {
        type: 'error', text: `Error: ${err.message || 'failed to start'}`,
      });
    }
  }, [addMessage, applyChunk, canSend, facilityId, isStreaming, armNavigate,
    resetNavigate, flushPreview, clearWatchdog, takeStoppedTurn]);

  // THE FRAME'S QUEUED TURN — "Walk me through it", or anything else
  // dispatched at the dock through `froggerai:ask`.
  //
  // ⚠️ IDENTITY IS THE KEY, NOT THE WORDS. The first cut gated on the
  // TEXT and cleared it after sending, and the same sentence asked twice
  // was then swallowed: the frame re-set a prop that had not been
  // cleared yet, React saw no change, and the second turn never
  // happened. `utteranceKey` is bumped per dispatch, so two identical
  // requests are two turns and a re-render is none.
  const sentKeyRef = useRef(null);
  useEffect(() => {
    const text = (initialUtterance || '').trim();
    if (!text) return;
    const key = utteranceKey === undefined ? text : utteranceKey;
    if (sentKeyRef.current === key) return;
    if (sendingRef.current || isStreaming) return;
    sentKeyRef.current = key;
    sendUtterance(text).finally(() => onUtteranceSent?.(text));
  }, [initialUtterance, utteranceKey, isStreaming, sendUtterance, onUtteranceSent]);

  /**
   * What the mic button is CURRENTLY SAYING, or null when it is saying
   * nothing (a desk's tap-to-record, or a session that is idle, ended
   * or paused). Stable, so `VoiceRecorder`'s reporting effect depends on
   * the label and not on this component's render count.
   */
  const handleVoiceStatus = useCallback((label) => {
    setVoiceStatus(label || null);
  }, []);

  const handleVoiceMessage = useCallback(async (audioBlob, duration) => {
    if (!canSend) throw new Error('No facility ID available');
    setError(null);
    setIsStreaming(true);
    // A NEW TURN OWNS THE SCREEN — spoken exactly as typed.
    resetNavigate();
    const userMessage = addMessage({
      type: 'user', content: `Voice message (${duration}s)`, isVoice: true,
    });
    const aiMessage = addMessage({ type: 'assistant', content: '', streaming: true });
    // A spoken turn has no Stop of its own, but it may be the request AFTER
    // a stopped typed turn, and it carries that report too (item E).
    const stoppedTurn = takeStoppedTurn();
    try {
      const response = await sendVoiceMessage(
        facilityId, audioBlob, duration, conversationIdRef.current,
        // The SAME place the typed path sends, read at send time.
        placeForWire(placeRef.current, nowhereRef.current),
        // ONE TURN, ONE RUN — the spoken turn's one-time id.
        newTurnId(),
        stoppedTurn,
      );
      const data = response.data || {};

      // The transcript replaces "Voice message (4s)" — what the keeper
      // wants to see is WHAT WAS HEARD, which is also the only way they
      // can tell a mis-hearing from a mis-understanding.
      if (data.transcript) {
        setMessages((prev) => prev.map((m) => (
          m.id === userMessage.id ? { ...m, content: `“${data.transcript}”` } : m)));
      }

      // THROUGH THE SAME HANDLERS AS A TYPED TURN. The voice endpoint
      // does not stream, so its JSON fields are replayed as chunks —
      // same read-back row, same Undo buttons, same navigation.
      // ⚠️ `asked` SURVIVES THE SPOKEN TRANSPORT TOO (S3-06.4
      // review). The voice endpoint answers with a `tools` list and
      // this replayed it as bare names, so a `createAnimal` that
      // stopped to ask drew the chip of a create that landed — on the
      // one transport where the keeper's hands are busy and the chip is
      // all there is. `toolChip` normalises both shapes, so a reply
      // from an older server (bare strings) still renders.
      (data.tools || []).forEach((entry) => {
        const tool = toolChip(entry);
        if (!tool.name) return;
        applyChunk(aiMessage.id, {
          type: 'tool_result', name: tool.name, success: true,
          asked: tool.asked,
          question: !!(entry && typeof entry === 'object' && entry.question),
        });
      });
      // `quiet` rides the spoken transport exactly as it rides a
      // streamed chunk — "stop" and "next" are said out loud more often
      // than they are typed. See `nodes.quiet_chunk`.
      applyChunk(aiMessage.id, {
        type: 'chunk',
        text: data.reply || '',
        quiet: !!data.quiet,
        // The spoken transport carries the flag as a sibling field
        // (run 6 round 2, V1) and goes through the SAME branch.
        narration_superseded: !!data.narration_superseded,
      });
      if (data.readback || (data.undo_tokens || []).length) {
        applyChunk(aiMessage.id, {
          type: 'readback',
          text: data.readback || '',
          undo_tokens: data.undo_tokens || [],
        });
      }
      // The round's buckets, spoken exactly as typed — the voice reply
      // carries `counts` as a sibling field and it goes through the
      // SAME branch, so there is one counts row in the app.
      if (data.counts) {
        applyChunk(aiMessage.id, { type: 'counts', ...data.counts });
      }
      if (data.navigate) {
        applyChunk(aiMessage.id, { type: 'navigate', to: data.navigate });
      }

      setMessages((prev) => prev.map((m) => (
        m.id === aiMessage.id
          ? {
            ...m,
            content: m.content || SILENT_TURN_TEXT,
            streaming: false,
            completedAt: new Date(),
          }
          : m)));
    } catch (err) {
      // A VOICE TURN IS A TURN — including when it fails, and including
      // when it never answers at all (run 13, F1).
      //
      // The spoken transport is a plain POST, so there is no silence to
      // watch — axios' own `timeout` (the SAME `STREAM_SILENCE_MS`) is
      // the watchdog, and a request that hits it gets the SAME sentence
      // a stalled SSE turn gets. `ECONNABORTED` is what axios raises on
      // its timeout; the message is checked too, because a fetch-based
      // adapter says `ETIMEDOUT` or nothing at all and the keeper must
      // not be told "Voice message failed: timeout of 120000ms
      // exceeded", which is a number rather than an answer.
      const timedOut = err?.code === 'ECONNABORTED' || err?.code === 'ETIMEDOUT'
        || /timeout/i.test(err?.message || '');
      // …and so does a request that got NO RESPONSE AT ALL (axios'
      // "Network Error": no `err.response`). The upload may well have
      // reached the server and run — "Voice message failed: Network
      // Error" reads like an instruction to say it again, which is the
      // second feeding. Nothing re-sends it; the keeper decides, told
      // the truth.
      const noResponse = !!err?.isAxiosError && !err?.response;
      // …and so does a PROXY that gave up, or a 500 from the view after
      // the graph ran (reviews of 9ca56cb3 and df642f3e): gunicorn's
      // `--timeout` is 120 s, so a voice turn killed there comes back
      // 502/504 after its writes may have landed. See `GATEWAY_STATUSES`.
      const gatewayGaveUp = GATEWAY_STATUSES.includes(err?.response?.status);
      applyChunk(aiMessage.id, {
        type: 'error',
        text: (timedOut || noResponse || gatewayGaveUp)
          ? NO_ANSWER_TEXT
          // A repeat the server refused (`turn_already_received`) is its
          // own sentence, never "Voice message failed: …".
          : (err.response?.data?.code === 'turn_already_received'
            ? err.response.data.detail
            : `Voice message failed: ${err.response?.data?.details || err.message}`),
      });
      throw err;
    } finally {
      setIsStreaming(false);
      // A VOICE TURN IS A TURN — including the preview line.
      flushPreview(aiMessage.id);
      // A VOICE TURN IS A TURN — including this. There is no `done`
      // chunk on this transport, so without the flush a spoken "open
      // Bruce" would wait on the timer alone.
      flushNavigate();
    }
  }, [addMessage, applyChunk, canSend, facilityId, flushNavigate, resetNavigate,
    flushPreview, takeStoppedTurn]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const msg = draft.trim();
    if (!msg) return;
    // ⚠️ **A MESSAGE TYPED WHILE A REPLY IS RUNNING IS NEVER DROPPED**
    // (Cowork on df05fc19, 2026-10-22: "thanks", typed during a running
    // turn, never appeared and was never answered). The draft used to be
    // cleared HERE and then handed to `sendUtterance`, whose guard
    // refuses a send while a turn holds the composer — silently, so the
    // words were gone. The composer is `disabled` while a turn runs (the
    // operator's 2026-09-13 ruling, and the voice session's own rule:
    // speech during THINKING is not captured); a submit that still
    // reaches here mid-turn is REFUSED VISIBLY — the words stay in the
    // box, a line under the row says why, and Send takes them once the
    // reply is done. Never queued: one send, the keeper's.
    if (sendingRef.current || isStreaming) {
      setHeldMidTurn(true);
      return;
    }
    setHeldMidTurn(false);
    // Cleared HERE and not inside `sendUtterance`: the Undo button has
    // no draft to clear, and clearing one it did not write would eat a
    // half-typed sentence the keeper is standing there dictating.
    setDraft('');
    await sendUtterance(msg);
  };

  // The refusal line belongs to the turn that refused; the box keeps the
  // words when it ends.
  useEffect(() => {
    if (!isStreaming) setHeldMidTurn(false);
  }, [isStreaming]);

  // S3-06.2 — Undo sends the ordinary utterance `undo <token>`, so the
  // reversal goes through the same graph, session and turn log. The
  // LABEL is what the bubble shows (S3-06.4); the token goes on the
  // wire and nowhere else the keeper can see.
  const handleUndo = async (token, label) => {
    if (!token || sendingRef.current || isStreaming) return;
    if (undoneTokens.has(token)) return;
    pendingUndoRef.current = token;
    await sendUtterance(`undo ${token}`, {
      display: `Undo${label ? ` ${label}` : ''}`,
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleStopStream = () => {
    if (activeStreamRef.current) {
      activeStreamRef.current.close?.();
      activeStreamRef.current = null;
    }
    // The keeper stopped it on purpose; nothing is waiting on silence,
    // and no turn owns the composer any more.
    //
    // ⚠️ **AND IT IS NO LONGER THE LATEST TURN** (re-review 4, item 1 —
    // reproduced). `close()` aborts the socket, but `navigate` and
    // `done` routinely arrive in ONE `reader.read()`, so a target
    // already in the buffer still reached `onMessage` after the Stop —
    // and with `latestTurnRef` still naming this turn it did not read
    // as superseded, so it MOVED THE SCREEN and re-pointed the chip
    // every write gate reads. A turn the keeper stopped on purpose is
    // superseded by definition: its own bubble is left exactly as it
    // is, and it says nothing to the screen. (`newConversation` has
    // carried this line since the turn ref existed.)
    //
    // ⚠️ AND IT STOPS STREAMING (fixer, 2026-10-22). Left `streaming`, the
    // bubble kept the waiting indicator spinning under a dead turn — and
    // the next send drew a second. Only the flag changes: the TEXT is left
    // exactly as it is (the pins in StreamWatchdog.test.js — a stopped
    // empty bubble stays empty, and a chunk already in flight still lands
    // in it), so nothing here claims what the server did.
    const stoppedId = currentTurnRef.current || latestTurnRef.current;
    if (stoppedId) {
      setMessages((prev) => prev.map((m) => (
        m.id === stoppedId && m.streaming ? { ...m, streaming: false } : m)));
      // ITEM E — the NEXT request reports what this bubble showed. Only a
      // Stop ever sets this; the text is read when that request is sent.
      if (turnIdsRef.current[stoppedId]) {
        stoppedTurnRef.current = {
          messageId: stoppedId, turnId: turnIdsRef.current[stoppedId],
        };
      }
    }
    clearWatchdog();
    currentTurnRef.current = null;
    latestTurnRef.current = null;
    sendingRef.current = false;
    setIsStreaming(false);
  };

  // THE WAITING INDICATOR BELONGS TO THE LIVE TURN ONLY: the newest
  // assistant bubble, while a turn is running. A bubble some other path
  // left `streaming` can never draw a second one.
  const liveTurnId = isStreaming
    ? [...messages].reverse().find((m) => m.type === 'assistant')?.id
    : undefined;

  return (
    <div
      className={`froggerai-panel${compact ? ' froggerai-panel--compact' : ''}`}
      data-froggerai-panel="1"
    >
      <div className="froggerai-panel__log" data-testid="froggerai-log">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`froggerai-row froggerai-row--${message.type}`}
          >
            <div className={`froggerai-bubble froggerai-bubble--${message.type}`}>
              {/* THE WAITING INDICATOR (waiting batch, 2026-10-21): while
                  a turn has NO TEXT yet, the bubble shows the customer
                  FroggyAI's shared indicator with the staff lines — dots,
                  then a quip after FIRST_ROTATION_MS, mood never status.
                  It sits OUTSIDE `.message-content` so the bubble's text
                  is still exactly what the turn said, and it goes the
                  moment the first chunk of text lands. The tool status
                  ("Calling getMadeFoods…") stays in the composer's row. */}
              {message.type === 'assistant' && message.streaming && !message.content
                && message.id === liveTurnId && (
                <div className="froggerai-working" data-testid="froggerai-working">
                  <FroggyWorking
                    lines={FROGGER_WORKING_LINES}
                    neutral={FROGGER_WORKING_NEUTRAL}
                    label={FROGGER_WORKING_SR}
                  />
                </div>
              )}
              {/* ⚠️ AN ANSWER IS MARKDOWN, DRAWN BY THE BUBBLE'S OWN
                  RENDERER (Cowork run 24, finding 8): the dock printed
                  `**bold**` as raw asterisks while the FroggyAI bubble
                  drew it bold. `ChatMarkdown` is the one renderer (bold,
                  italics, lists, links; no raw HTML). The keeper's own
                  words stay plain text, exactly as typed. */}
              <div
                className="message-content"
                style={message.type === 'assistant'
                  ? undefined : { whiteSpace: 'pre-wrap' }}
                data-message-markdown={message.type === 'assistant' ? '1' : undefined}
              >
                {message.type === 'assistant'
                  ? <ChatMarkdown lineBreaks>{message.content}</ChatMarkdown>
                  : message.content}
                {message.streaming && message.content && (
                  <span className="spinner-border spinner-border-sm ms-1" role="status">
                    <span className="visually-hidden">Streaming…</span>
                  </span>
                )}
              </div>

              {/* THE ROUND'S BUCKETS, under the answer (re-sweep item
                  3). Its own quiet row, the same family as the read-back
                  receipt below: the model's sentence is prose and this
                  is the count, and the operator comparing it with the
                  Tasks Today header must be reading the same numbers. */}
              {/* ⚠️ NOT GATED ON `!streaming` ANY MORE (batch 8, item
                  G). The server now emits this chunk from
                  `execute_tools` — the moment the tool RESULT lands,
                  not after the model has written its paragraph — and
                  hiding it until the turn was over threw that away and
                  left the panel blank for the 17 seconds the fix exists
                  to remove. The model's sentence still arrives, under
                  it, as the last thing. */}
              {countsLine(message.counts) && (
                <div
                  className="froggerai-counts"
                  data-testid="froggerai-counts"
                  data-counts={countsLine(message.counts)}
                >
                  <small className="text-muted">{countsLine(message.counts)}</small>
                  {/* The FIRST card of the round, so the panel says
                      something the keeper can act on before the prose
                      arrives. The server sends it or it is absent; it is
                      never composed here. */}
                  {message.counts.first && (
                    <small
                      className="froggerai-counts__first"
                      data-testid="froggerai-counts-first"
                    >
                      {message.counts.first}
                    </small>
                  )}
                </div>
              )}

              {/* The tools a turn actually called. Visible, because
                  "which tool answered that" is the first question when
                  an answer looks wrong, and the turn log is not on the
                  screen the keeper is holding. */}
              {showToolChips && (message.tools || []).length > 0 && !message.streaming && (
                <div className="froggerai-tools" data-testid="froggerai-tools">
                  {/* An entry `toolChip` cannot read becomes a blank
                      name — and a blank name still drew an empty
                      <code> box. Dropped here rather than in
                      `toolChip`, which owes its callers a shape. */}
                  {message.tools.map(toolChip)
                    .filter(({ name }) => name)
                    .map(({ name, asked, id }, i) => (
                    <code
                      // Keyed by the tool_call id when the panel paired
                      // one (run 25, F8); never by chunk.
                      // eslint-disable-next-line react/no-array-index-key
                      key={id || `${name}-${i}`}
                      className={asked
                        ? 'froggerai-tool froggerai-tool--asked'
                        : 'froggerai-tool'}
                      data-asked={asked ? 'true' : undefined}
                      title={asked
                        ? 'asked a question, wrote nothing'
                        : undefined}
                    >
                      {/* H (batch 8) — A QUESTION MARK, because the
                          dashed outline is a difference you have to
                          STOP to see. Glanced at on a phone at a rack,
                          `createAnimal` and `createAnimal` read the
                          same; `createAnimal?` does not. The style and
                          the title stay: this is a third signal, not a
                          replacement for them. */}
                      {asked ? `${name}?` : name}
                    </code>
                  ))}
                </div>
              )}

              {/* S3-06.2 — the read-back line and its Undo. A SEPARATE,
                  quieter row under the bubble, not part of the answer
                  text: it is the receipt for what was written. */}
              {(message.readback || (message.undoTokens || []).length > 0)
                && !message.streaming && (
                <div className="froggerai-readback" data-testid="froggerai-readback">
                  {message.readback && (
                    <small className="text-muted">{message.readback}</small>
                  )}
                  {(message.undoTokens || []).map((entry) => {
                    // SPENT IS SPENT (S3-06.4). Keyed by the TOKEN, so
                    // a second read-back in the same turn keeps its own
                    // button live, and so a restored transcript knows
                    // which of its buttons have already been pressed.
                    const spent = undoneTokens.has(entry.token);
                    // ⚠️ A GREYED CONTROL SAYS WHY (re-review 4, item
                    // 2). An Undo is disabled for two different
                    // reasons and only one of them was on the button:
                    // `spent` said "Already undone", and a live turn
                    // running over it said NOTHING — which on a
                    // superseded turn (whose receipt renders while the
                    // NEXT turn streams) is the one the keeper is most
                    // likely to be looking at. `Already undone` is the
                    // stronger fact and still wins.
                    //
                    // It is the TITLE and not `aria-label`: the
                    // accessible NAME stays what the button DOES
                    // ("Undo fed Rack 1", its own text), because a
                    // disabled control renamed to its excuse cannot be
                    // found by the thing it is for — and a screen
                    // reader already announces the disabled state
                    // beside it. The 44px coarse target is untouched.
                    const why = spent
                      ? 'Already undone'
                      : (isStreaming
                        ? 'Wait for the current answer to finish'
                        : (entry.label ? `Undo ${entry.label}` : 'Undo'));
                    return (
                      <button
                        type="button"
                        key={entry.token}
                        className="ff-btn ff-btn--secondary ff-btn--sm froggerai-undo"
                        disabled={isStreaming || spent}
                        data-undo-token={entry.token}
                        data-undone={spent ? '1' : '0'}
                        onClick={() => handleUndo(entry.token, entry.label)}
                        title={why}
                      >
                        {spent ? 'Undone'
                          : `Undo${entry.label ? ` ${entry.label}` : ''}`}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        ))}

        {error && (
          <Alert variant="danger" className="py-1 px-2 mb-2">
            <small>{error}</small>
          </Alert>
        )}

        <div ref={messagesEndRef} />
      </div>

      <div className="froggerai-panel__composer">
        <Form onSubmit={handleSubmit}>
          {/* THE PLACE, ON A ROW OF ITS OWN (operator, phone pass
              2026-09-13). The frame decides when it earns one — the
              dock hands it here only on a phone and only when there IS
              a place; with none, the empty chip rides in the composer
              row below and the collapsed dock is still ONE row. Full
              width, so the name wraps instead of ellipsizing away. */}
          {chipRow && <div className="froggerai-chiprow">{chipRow}</div>}
          <div
            className={`froggerai-composer-row${isStreaming ? ' froggerai-composer-row--busy' : ''}`}
          >
            {/* I — THE COLLAPSED DOCK IS ONE ROW (S3-06.4). The place
                chip used to sit in the dock's own head ABOVE this row,
                so a "collapsed" phone dock was two rows and 111px of a
                844px screen. The frame hands it in here instead: a
                compact pill at the left of the composer, ellipsized,
                capped at 40% of the row, and still the tap target that
                clears the place. */}
            {chipSlot}
            {/* ⚠️ THE PLACEHOLDER IS NOT A STATUS LINE (operator,
                2026-09-13). It used to read "Working…" while a turn was
                in flight, which made it the THIRD surface saying so on
                a 390px row — beside the mic's "Thinking" and a spinner
                row of its own. The box is `disabled` while streaming,
                which is that state said plainly and without words. */}
            <Form.Control
              ref={inputRef}
              as="textarea"
              rows={1}
              placeholder={placeholder}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isStreaming || !canSend}
              aria-label="Ask FroggerAI"
              data-froggerai-input="1"
            />
            {/* In the dock this is CONVERSATION MODE — one tap opens a
                listening session that segments speech itself and keeps
                the mic open across turns. On a desk (`compact` false)
                it stays tap-to-record. Either way the segment goes to
                the SAME Whisper endpoint, as an ordinary turn. */}
            <VoiceRecorder
              onVoiceMessage={handleVoiceMessage}
              // The ONE status token during a voice session: the mic
              // reports what its label says, and the spinner row below
              // stands down for as long as it says anything.
              onStatusChange={handleVoiceStatus}
              disabled={isStreaming || !canSend}
              compact={compact}
              maxDurationSeconds={voiceConfig.maxDurationSeconds}
              recommendedMaxSeconds={voiceConfig.recommendedMaxSeconds}
            />
            {isStreaming ? (
              <button type="button" className="ff-btn ff-btn--danger" onClick={handleStopStream} disabled={!canSend}>
                Stop
              </button>
            ) : (
              // R-UI-LEFTOVERS B3: SECONDARY. The composer's ONE primary
              // is the mic (Batch 34 C4: "the keeper's dock keeps its mic
              // as the primary"); Send is the ordinary typed act beside it.
              <button type="submit" className="ff-btn ff-btn--secondary" disabled={!draft.trim() || !canSend}>
                Send
              </button>
            )}
          </div>

          {/* ONE TOKEN, AND THE MIC HAS IT FIRST. This row is the
              status for a TYPED turn; during a live voice session the
              mic button is already saying it in the row above, so a
              second copy here is a second row on the smallest screen in
              the app for a word the keeper has already read. */}
          {/* A SEND REFUSED MID-TURN SAYS SO (Cowork on df05fc19): the
              words are still in the box, and nothing was sent. */}
          {heldMidTurn && isStreaming && (
            <div className="froggerai-held" aria-live="polite" data-testid="froggerai-held">
              {HELD_MID_TURN_TEXT}
            </div>
          )}

          {isStreaming && !voiceStatus && (
            <div className="froggerai-status" role="status" data-testid="froggerai-status">
              <span className="spinner-border spinner-border-sm me-1" />
              {activeToolCall ? (
                <>
                  {'Calling '}
                  <code>{activeToolCall}</code>
                </>
              ) : 'Thinking…'}
            </div>
          )}

          {!canSend && (
            <div className="froggerai-status">
              No facility here — open a facility page.
            </div>
          )}
        </Form>
      </div>

      {!compact && (
        <div className="froggerai-panel__foot">
          <button
            type="button"
            className="ff-btn ff-btn--secondary ff-btn--sm"
            onClick={handleNewConversation}
            disabled={isStreaming}
          >
            New conversation
          </button>
        </div>
      )}
    </div>
  );
});

FroggerAIChatPanel.displayName = 'FroggerAIChatPanel';

FroggerAIChatPanel.propTypes = {
  facilityId: PropTypes.string,
  facilityName: PropTypes.string,
  /** False while the frame is closed: no summary fetch, no voice config. */
  active: PropTypes.bool,
  place: PropTypes.object,
  /** The keeper tapped × and no page has set a place since (item 10). */
  placeNowhere: PropTypes.bool,
  compact: PropTypes.bool,
  /** The frame's place chip, rendered INSIDE the composer row. */
  chipSlot: PropTypes.node,
  /**
   * A FULL-WIDTH ROW ABOVE the composer, for a chip too important to
   * squeeze (the dock's place chip on a phone). Null keeps the collapsed
   * dock at one row; the frame decides which, not this component.
   */
  chipRow: PropTypes.node,
  initialUtterance: PropTypes.string,
  /** Bumped per dispatch so the SAME words asked twice are two turns. */
  utteranceKey: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onUtteranceSent: PropTypes.func,
  onNavigate: PropTypes.func,
  /** ONCE per turn, at the first sign of an answer. Batch 8, item A. */
  onAnswer: PropTypes.func,
  /** The one line a COLLAPSED frame shows: read-back, else prose. */
  onPreview: PropTypes.func,
  onNewConversation: PropTypes.func,
  showWelcome: PropTypes.bool,
  placeholder: PropTypes.string,
};

FroggerAIChatPanel.defaultProps = {
  facilityId: null,
  facilityName: '',
  active: true,
  place: null,
  placeNowhere: false,
  compact: false,
  chipSlot: null,
  chipRow: null,
  initialUtterance: '',
  utteranceKey: undefined,
  onUtteranceSent: undefined,
  onNavigate: undefined,
  onAnswer: undefined,
  onPreview: undefined,
  onNewConversation: undefined,
  showWelcome: true,
  // ⚠️ IT HAS TO FIT THE DOCK (Cowork run 33, D6). "Ask, or say what you
  // did…" is ~188px at 16px; the composer beside the 1280 dock's chip, mic
  // and send leaves 128px, and a one-row textarea WRAPS a placeholder, so
  // the second half was cut off mid-word. "Ask or log…" is ~85px: the two
  // things the box is for, whole, at every width the dock draws.
  placeholder: COMPOSER_PLACEHOLDER,
};

export default FroggerAIChatPanel;
